import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import Fill from 'ol/style/Fill';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import DragPan from 'ol/interaction/DragPan';
import {getDistance as getGeodesicDistance} from 'ol/sphere';
import {fromLonLat, toLonLat} from 'ol/proj';

// Maximum number of persisted measurement lines allowed on the map and in URL state.
export const MEASURE_MAX_LINES = 10;

function parseStrictNumber(rawValue) {
  if (typeof rawValue !== 'string') return null;
  const trimmed = rawValue.trim();
  if (!trimmed) return null;
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function isValidLatitude(latitude) {
  return Number.isFinite(latitude) && latitude >= -90 && latitude <= 90;
}

function isValidLongitude(longitude) {
  return Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
}

// Parse a URL line parameter in the form "lat1,lon1,lat2,lon2".
export function parseMeasureLineQueryValue(rawValue) {
  if (typeof rawValue !== 'string') return null;
  const parts = rawValue.split(',').map(part => part.trim());
  if (parts.length !== 4) return null;

  const lat1 = parseStrictNumber(parts[0]);
  const lon1 = parseStrictNumber(parts[1]);
  const lat2 = parseStrictNumber(parts[2]);
  const lon2 = parseStrictNumber(parts[3]);
  if (!isValidLatitude(lat1) || !isValidLongitude(lon1) || !isValidLatitude(lat2) || !isValidLongitude(lon2)) {
    return null;
  }

  return { lat1, lon1, lat2, lon2 };
}

function formatDistanceLabel(kilometers) {
  if (!Number.isFinite(kilometers)) return '0 km';
  if (kilometers >= 100) return `${kilometers.toFixed(1)} km`;
  if (kilometers >= 10) return `${kilometers.toFixed(2)} km`;
  return `${kilometers.toFixed(3)} km`;
}

function midpointCoordinate(coordA, coordB) {
  return [
    (coordA[0] + coordB[0]) / 2,
    (coordA[1] + coordB[1]) / 2
  ];
}

function createEndpointFeature(mapCoordinate) {
  const endpointFeature = new Feature({
    geometry: new Point(mapCoordinate),
    isMeasureToolFeature: true
  });

  endpointFeature.setStyle(new Style({
    text: new Text({
      text: 'x',
      font: 'bold 14px Arial',
      fill: new Fill({color: '#1a1a1a'}),
      stroke: new Stroke({color: '#ffffff', width: 2}),
      textAlign: 'center',
      textBaseline: 'middle'
    })
  }));

  return endpointFeature;
}

function createLineFeature(startCoordinate, endCoordinate) {
  const lineFeature = new Feature({
    geometry: new LineString([startCoordinate, endCoordinate]),
    isMeasureToolFeature: true,
    isMeasureLine: true
  });

  lineFeature.setStyle(new Style({
    stroke: new Stroke({
      color: 'rgba(24, 24, 24, 0.95)',
      width: 2
    })
  }));

  return lineFeature;
}

function createLabelFeature(startCoordinate, endCoordinate, labelText) {
  const labelPoint = midpointCoordinate(startCoordinate, endCoordinate);
  const labelFeature = new Feature({
    geometry: new Point(labelPoint),
    isMeasureToolFeature: true,
    isMeasureLabel: true
  });

  labelFeature.setStyle(new Style({
    text: new Text({
      text: labelText,
      font: 'bold 12px Arial',
      fill: new Fill({color: '#101010'}),
      stroke: new Stroke({color: '#ffffff', width: 3}),
      offsetY: -12,
      textAlign: 'center',
      textBaseline: 'bottom'
    })
  }));

  return labelFeature;
}

function buildMeasurementRecord(startLonLat, endLonLat, startCoordinate, endCoordinate) {
  const distanceMeters = getGeodesicDistance(
    [startLonLat[0], startLonLat[1]],
    [endLonLat[0], endLonLat[1]]
  );
  const distanceKm = distanceMeters / 1000;
  const labelText = formatDistanceLabel(distanceKm);

  const lineFeature = createLineFeature(startCoordinate, endCoordinate);
  const startEndpointFeature = createEndpointFeature(startCoordinate);
  const endEndpointFeature = createEndpointFeature(endCoordinate);
  const labelFeature = createLabelFeature(startCoordinate, endCoordinate, labelText);

  const features = [lineFeature, startEndpointFeature, endEndpointFeature, labelFeature];
  features.forEach(feature => {
    feature.set('measureFeatureKey', `${Date.now()}-${Math.random()}`);
  });

  return {
    startLonLat: [startLonLat[0], startLonLat[1]],
    endLonLat: [endLonLat[0], endLonLat[1]],
    distanceKm,
    features,
    lineFeature
  };
}

export function createMeasureToolManager({
  map,
  onLinesChanged,
  onStatus,
  onModeChange
}) {
  // Dedicated layer/source for all measure-tool graphics.
  const source = new VectorSource();
  const layer = new VectorLayer({
    source,
    declutter: false,
    zIndex: 160
  });

  // Internal tool state for draw mode and completed lines.
  const state = {
    isActive: false,
    isDrawing: false,
    startCoordinate: null,
    startLonLat: null,
    rubberBandLineFeature: null,
    completedLines: [],
    suspendedInteractions: []
  };

  function suspendMapInteractionsForMeasureMode() {
    state.suspendedInteractions = [];
    map.getInteractions().forEach((interaction) => {
      if (!interaction?.getActive || !interaction?.setActive) return;
      if (interaction instanceof DragPan && interaction.getActive()) {
        interaction.setActive(false);
        state.suspendedInteractions.push(interaction);
      }
    });
  }

  function restoreMapInteractionsAfterMeasureMode() {
    state.suspendedInteractions.forEach((interaction) => {
      try {
        interaction.setActive(true);
      } catch (_error) {
        // Ignore restoration errors from disposed interactions.
      }
    });
    state.suspendedInteractions = [];
  }

  function emitStatus(message) {
    if (typeof onStatus === 'function' && message) {
      onStatus(message);
    }
  }

  function emitLinesChanged() {
    if (typeof onLinesChanged === 'function') {
      onLinesChanged(getSerializedLines());
    }
  }

  function emitModeChanged() {
    if (typeof onModeChange === 'function') {
      onModeChange({
        isActive: state.isActive,
        isDrawing: state.isDrawing,
        lineCount: state.completedLines.length
      });
    }
  }

  function setMapCursor(cursorValue) {
    const mapTarget = map.getTargetElement();
    if (!mapTarget?.style) return;
    mapTarget.style.cursor = cursorValue || '';
  }

  function clearRubberBand() {
    if (state.rubberBandLineFeature) {
      source.removeFeature(state.rubberBandLineFeature);
      state.rubberBandLineFeature = null;
    }
  }

  function resetDrawingState() {
    clearRubberBand();
    state.isDrawing = false;
    state.startCoordinate = null;
    state.startLonLat = null;
  }

  // Activate draw mode and switch cursor to crosshair.
  function activate() {
    if (state.completedLines.length >= MEASURE_MAX_LINES) {
      emitStatus(`Measure line limit reached (${MEASURE_MAX_LINES})`);
      return false;
    }
    state.isActive = true;
    state.isDrawing = false;
    state.startCoordinate = null;
    state.startLonLat = null;
    suspendMapInteractionsForMeasureMode();
    setMapCursor('crosshair');
    emitModeChanged();
    return true;
  }

  // Deactivate draw mode and restore normal map interactions.
  function deactivate() {
    resetDrawingState();
    state.isActive = false;
    restoreMapInteractionsAfterMeasureMode();
    setMapCursor('');
    emitModeChanged();
  }

  function cancel() {
    deactivate();
  }

  function shouldBlockMapInteractions() {
    return state.isActive;
  }

  function getSerializedLines() {
    return state.completedLines.map(record => ({
      lat1: record.startLonLat[1],
      lon1: record.startLonLat[0],
      lat2: record.endLonLat[1],
      lon2: record.endLonLat[0],
      distanceKm: record.distanceKm
    }));
  }

  // Add a validated line directly (used by startup URL restoration).
  function addLineFromLonLat(startLonLat, endLonLat) {
    if (state.completedLines.length >= MEASURE_MAX_LINES) {
      return false;
    }

    const startCoordinate = fromLonLat(startLonLat);
    const endCoordinate = fromLonLat(endLonLat);
    const record = buildMeasurementRecord(startLonLat, endLonLat, startCoordinate, endCoordinate);

    if (record.distanceKm <= 0) {
      return false;
    }

    record.features.forEach(feature => source.addFeature(feature));
    state.completedLines.push(record);
    emitLinesChanged();
    emitModeChanged();
    return true;
  }

  function removeRecordAtIndex(index) {
    if (index < 0 || index >= state.completedLines.length) return false;
    const [record] = state.completedLines.splice(index, 1);
    record.features.forEach(feature => source.removeFeature(feature));
    emitLinesChanged();
    emitModeChanged();
    return true;
  }

  // Remove a line by any feature belonging to that line (line, endpoint marker, or label).
  function removeLineByFeature(feature) {
    if (!feature?.get || !feature.get('isMeasureToolFeature')) return false;

    const index = state.completedLines.findIndex(record => record.features.includes(feature));
    if (index === -1) return false;
    return removeRecordAtIndex(index);
  }

  function loadLinesFromQuery(lineList) {
    if (!Array.isArray(lineList)) return;

    lineList.slice(0, MEASURE_MAX_LINES).forEach(line => {
      const startLonLat = [line.lon1, line.lat1];
      const endLonLat = [line.lon2, line.lat2];
      addLineFromLonLat(startLonLat, endLonLat);
    });
  }

  function handlePointerDown(event) {
    if (!state.isActive) return;
    if (event.originalEvent?.button !== 0) return;

    event.originalEvent?.preventDefault?.();

    state.isDrawing = true;
    state.startCoordinate = event.coordinate;
    state.startLonLat = toLonLat(event.coordinate);

    clearRubberBand();
    state.rubberBandLineFeature = new Feature({
      geometry: new LineString([event.coordinate, event.coordinate]),
      isMeasureToolFeature: true
    });
    state.rubberBandLineFeature.setStyle(new Style({
      stroke: new Stroke({
        color: 'rgba(16, 16, 16, 0.8)',
        width: 2,
        lineDash: [8, 6]
      })
    }));
    source.addFeature(state.rubberBandLineFeature);
    emitModeChanged();
  }

  function handlePointerMove(event) {
    if (!state.isActive || !state.isDrawing || !state.rubberBandLineFeature) return;
    event.originalEvent?.preventDefault?.();
    const geometry = state.rubberBandLineFeature.getGeometry();
    if (!geometry) return;
    geometry.setCoordinates([state.startCoordinate, event.coordinate]);
  }

  function handlePointerUp(event) {
    if (!state.isActive || !state.isDrawing || !state.startCoordinate || !state.startLonLat) return;
    event.originalEvent?.preventDefault?.();

    const endCoordinate = event.coordinate;
    const endLonLat = toLonLat(endCoordinate);
    const startLonLat = [state.startLonLat[0], state.startLonLat[1]];

    resetDrawingState();

    const distanceMeters = getGeodesicDistance(startLonLat, endLonLat);
    if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) {
      deactivate();
      return;
    }

    if (state.completedLines.length >= MEASURE_MAX_LINES) {
      emitStatus(`Measure line limit reached (${MEASURE_MAX_LINES})`);
      deactivate();
      return;
    }

    addLineFromLonLat(startLonLat, endLonLat);
    deactivate();
  }

  function handleDocumentPointerDown(event) {
    if (!state.isActive || state.isDrawing) return;

    // Let the measure-tool toggle button click handler own cancel/activate behavior.
    if (event.target?.closest?.('#measure-tool-button')) {
      return;
    }

    const mapTarget = map.getTargetElement();
    if (!mapTarget) return;
    const clickedInsideMap = mapTarget.contains(event.target);
    if (!clickedInsideMap) {
      deactivate();
    }
  }

  function handleEscapeKey(event) {
    if (!state.isActive) return;
    if (event.key === 'Escape') {
      deactivate();
    }
  }

  map.on('pointerdown', handlePointerDown);
  map.on('pointermove', handlePointerMove);
  map.on('pointerup', handlePointerUp);
  document.addEventListener('mousedown', handleDocumentPointerDown, true);
  document.addEventListener('keydown', handleEscapeKey);

  return {
    layer,
    activate,
    cancel,
    isActive: () => state.isActive,
    isDrawing: () => state.isDrawing,
    shouldBlockMapInteractions,
    removeLineByFeature,
    loadLinesFromQuery,
    getSerializedLines,
    getLineCount: () => state.completedLines.length
  };
}
